// FAIR (Open FAIR / Jack Jones methodology) calculation engine.
// Ported near-verbatim from the standalone FAIR Assessor app (utils/fair.ts) so the
// same risk analysis produces the same results in both places.
import type {
  RatingLevel, QualitativeInputs, QuantitativeInputs, QuantitativeRange,
  FairResults, MonteCarloResult, CfAnchor,
} from '../types/fair';

// --- Rating helpers ---------------------------------------------------------

export const RATING_LABELS: Record<RatingLevel, string> = {
  VL: 'Very Low',
  L: 'Low',
  M: 'Medium',
  H: 'High',
  VH: 'Very High',
};

export const RATING_ORDER: RatingLevel[] = ['VL', 'L', 'M', 'H', 'VH'];

export const ratingToIndex = (r: RatingLevel): number => RATING_ORDER.indexOf(r);

export const indexToRating = (i: number): RatingLevel => RATING_ORDER[Math.min(4, Math.max(0, Math.round(i)))];

// --- FAIR factor combination tables -----------------------------------------

/**
 * Multiplicative combination table (used for TEF and LEF).
 * Models: A x B where both A and B are probabilities/frequencies.
 * Open FAIR: TEF = ContactFreq x ProbabilityOfAction; LEF = TEF x Vulnerability
 */
const COMBINE_TABLE: RatingLevel[][] = [
  // other: VL    L     M     H     VH
  ['VL', 'VL', 'VL', 'L', 'L'],   // self: VL
  ['VL', 'L', 'L', 'M', 'M'],     // self: L
  ['VL', 'L', 'M', 'M', 'H'],     // self: M
  ['L', 'M', 'M', 'H', 'H'],      // self: H
  ['L', 'M', 'H', 'H', 'VH'],     // self: VH
];

export function combineFactors(a: RatingLevel, b: RatingLevel): RatingLevel {
  return COMBINE_TABLE[ratingToIndex(a)][ratingToIndex(b)];
}

/**
 * Additive combination table (used for Loss Magnitude).
 * Open FAIR: PLM + SLM are additive across loss event categories.
 */
const LM_ADD_TABLE: RatingLevel[][] = [
  // other: VL    L     M     H     VH
  ['VL', 'L', 'M', 'H', 'VH'],    // self: VL
  ['L', 'L', 'M', 'H', 'VH'],     // self: L
  ['M', 'M', 'H', 'H', 'VH'],     // self: M
  ['H', 'H', 'H', 'VH', 'VH'],    // self: H
  ['VH', 'VH', 'VH', 'VH', 'VH'], // self: VH
];

export function addLossMagnitudes(a: RatingLevel, b: RatingLevel): RatingLevel {
  return LM_ADD_TABLE[ratingToIndex(a)][ratingToIndex(b)];
}

/** Vulnerability = Threat Capability vs Control Strength. */
export function calcVulnerability(threatCap: RatingLevel, controlStr: RatingLevel): RatingLevel {
  const diff = ratingToIndex(threatCap) - ratingToIndex(controlStr);
  const idx = Math.min(4, Math.max(0, Math.round((diff + 4) / 2)));
  return RATING_ORDER[idx];
}

/** Primary Loss Magnitude: fold-combines all 6 categories using the additive LM table. */
export function calcPrimaryLoss(q: QualitativeInputs): RatingLevel {
  const ratings: RatingLevel[] = [
    q.productivity, q.response, q.replacement,
    q.competitiveAdvantage, q.finesJudgements, q.reputation,
  ];
  ratings.sort((a, b) => ratingToIndex(b) - ratingToIndex(a));
  return ratings.reduce((total, r) => addLossMagnitudes(total, r));
}

/** Secondary Loss = Secondary LEF x Secondary LM (combined multiplicatively). */
export function calcSecondaryLoss(q: QualitativeInputs): RatingLevel {
  const slm = addLossMagnitudes(q.secondaryLegal, q.secondaryRegulatory);
  return combineFactors(q.secondaryLef, slm);
}

// --- Qualitative scoring -----------------------------------------------------

export function calcQualitativeResults(q: QualitativeInputs): Pick<FairResults,
  'tef' | 'vulnerability' | 'lef' | 'primaryLoss' | 'secondaryLoss' | 'totalLoss' |
  'qualitativeScore' | 'qualitativeLabel' | 'riskColor'> {

  const tef = combineFactors(q.contactFrequency, q.probabilityOfAction);
  const vuln = calcVulnerability(q.threatCapability, q.controlStrength);
  const lef = combineFactors(tef, vuln);
  const primaryLoss = calcPrimaryLoss(q);
  const secondaryLoss = calcSecondaryLoss(q);
  const totalLoss = addLossMagnitudes(primaryLoss, secondaryLoss);
  const risk = combineFactors(lef, totalLoss);

  const scoreMap: Record<RatingLevel, number> = { VL: 10, L: 30, M: 50, H: 75, VH: 95 };
  const labelMap: Record<RatingLevel, FairResults['qualitativeLabel']> = {
    VL: 'Very Low', L: 'Low', M: 'Medium', H: 'High', VH: 'Very High',
  };
  const colorMap: Record<RatingLevel, FairResults['riskColor']> = {
    VL: 'success', L: 'info', M: 'warning', H: 'danger', VH: 'dark',
  };

  return {
    tef,
    vulnerability: vuln,
    lef,
    primaryLoss,
    secondaryLoss,
    totalLoss,
    qualitativeScore: scoreMap[risk],
    qualitativeLabel: labelMap[risk],
    riskColor: colorMap[risk],
  };
}

// --- PERT distribution sampler ------------------------------------------------

function pertSample(min: number, ml: number, max: number): number {
  if (min >= max) return ml;
  const mu = (min + 4 * ml + max) / 6;
  const sigma = (max - min) / 6;
  const u1 = Math.random() || 1e-10;
  const u2 = Math.random() || 1e-10;
  const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
  return Math.min(max, Math.max(min, mu + sigma * z));
}

// --- Monte Carlo engine --------------------------------------------------------

export function runMonteCarlo(q: QuantitativeInputs, iterations = 10000): MonteCarloResult {
  const aleValues: number[] = [];
  const lefValues: number[] = [];

  for (let i = 0; i < iterations; i++) {
    const cf = Math.max(0, pertSample(q.contactFrequencyPerYear.min, q.contactFrequencyPerYear.mostLikely, q.contactFrequencyPerYear.max));
    const pa = Math.min(1, Math.max(0, pertSample(q.probabilityOfAction.min, q.probabilityOfAction.mostLikely, q.probabilityOfAction.max)));
    const tc = Math.min(1, Math.max(0, pertSample(q.threatCapability.min, q.threatCapability.mostLikely, q.threatCapability.max)));
    const cs = Math.min(1, Math.max(0, pertSample(q.controlStrength.min, q.controlStrength.mostLikely, q.controlStrength.max)));

    const tef = cf * pa;
    // Open FAIR quantitative: Vulnerability = P(ThreatCapability > ResistanceStrength),
    // modeled per-iteration as a Bernoulli outcome converging to the true probability
    // across all iterations.
    const vuln = tc > cs ? 1.0 : 0.0;
    const lef = tef * vuln;

    const pm = Math.max(0, pertSample(q.primaryLossMagnitude.min, q.primaryLossMagnitude.mostLikely, q.primaryLossMagnitude.max));
    const sm = Math.max(0, pertSample(q.secondaryLossMagnitude.min, q.secondaryLossMagnitude.mostLikely, q.secondaryLossMagnitude.max));
    const lm = pm + sm;

    lefValues.push(lef);
    aleValues.push(lef * lm);
  }

  aleValues.sort((a, b) => a - b);
  lefValues.sort((a, b) => a - b);

  const pct = (arr: number[], p: number) => arr[Math.floor(p * arr.length)];
  const mean = (arr: number[]) => arr.reduce((a, b) => a + b, 0) / arr.length;

  const aleMin = pct(aleValues, 0.01);
  const aleMax = pct(aleValues, 0.99);
  const bucketSize = (aleMax - aleMin) / 20 || 1;
  const histogram = new Array(20).fill(0);
  for (const v of aleValues) {
    const b = Math.min(19, Math.floor((v - aleMin) / bucketSize));
    if (b >= 0) histogram[b]++;
  }

  return {
    iterations,
    annualLossExposure: {
      p10: pct(aleValues, 0.1),
      p50: pct(aleValues, 0.5),
      p90: pct(aleValues, 0.9),
      mean: mean(aleValues),
    },
    lossEventFrequency: {
      p10: pct(lefValues, 0.1),
      p50: pct(lefValues, 0.5),
      p90: pct(lefValues, 0.9),
      mean: mean(lefValues),
    },
    histogram,
  };
}

// --- Formatting helpers ---------------------------------------------------------

export function formatCurrency(v: number): string {
  if (v >= 1_000_000) return `€${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `€${(v / 1_000).toFixed(1)}K`;
  return `€${v.toFixed(0)}`;
}

// --- Qualitative-to-quantitative range derivation --------------------------------

const CF_RATING_TO_RANGE: Record<RatingLevel, QuantitativeRange> = {
  VL: { min: 0.2, mostLikely: 0.5, max: 1.0 },
  L: { min: 0.5, mostLikely: 1.5, max: 3.0 },
  M: { min: 1.0, mostLikely: 2.0, max: 4.0 },
  H: { min: 1.5, mostLikely: 3.0, max: 6.0 },
  VH: { min: 2.0, mostLikely: 4.0, max: 8.0 },
};

const PA_RATING_TO_RANGE: Record<RatingLevel, QuantitativeRange> = {
  VL: { min: 0.01, mostLikely: 0.05, max: 0.10 },
  L: { min: 0.05, mostLikely: 0.15, max: 0.30 },
  M: { min: 0.20, mostLikely: 0.35, max: 0.60 },
  H: { min: 0.50, mostLikely: 0.65, max: 0.85 },
  VH: { min: 0.75, mostLikely: 0.90, max: 0.99 },
};

const TC_RATING_TO_RANGE: Record<RatingLevel, QuantitativeRange> = {
  VL: { min: 0.05, mostLikely: 0.15, max: 0.25 },
  L: { min: 0.15, mostLikely: 0.28, max: 0.40 },
  M: { min: 0.30, mostLikely: 0.50, max: 0.65 },
  H: { min: 0.55, mostLikely: 0.68, max: 0.85 },
  VH: { min: 0.75, mostLikely: 0.88, max: 0.99 },
};

const CS_RATING_TO_RANGE: Record<RatingLevel, QuantitativeRange> = {
  VL: { min: 0.05, mostLikely: 0.15, max: 0.25 },
  L: { min: 0.15, mostLikely: 0.30, max: 0.45 },
  M: { min: 0.30, mostLikely: 0.50, max: 0.65 },
  H: { min: 0.50, mostLikely: 0.65, max: 0.80 },
  VH: { min: 0.70, mostLikely: 0.82, max: 0.95 },
};

export function cfRatingToRange(r: RatingLevel): QuantitativeRange { return CF_RATING_TO_RANGE[r]; }
export function paRatingToRange(r: RatingLevel): QuantitativeRange { return PA_RATING_TO_RANGE[r]; }
export function tcRatingToRange(r: RatingLevel): QuantitativeRange { return TC_RATING_TO_RANGE[r]; }
export function csRatingToRange(r: RatingLevel): QuantitativeRange { return CS_RATING_TO_RANGE[r]; }

const CF_ANCHOR_TO_RANGE: Record<CfAnchor, QuantitativeRange> = {
  '1-2': { min: 0.5, mostLikely: 1.5, max: 3.0 },
  '5-10': { min: 2.0, mostLikely: 5.0, max: 10.0 },
  '12': { min: 6.0, mostLikely: 10.0, max: 16.0 },
  '52': { min: 15, mostLikely: 25, max: 45 },
  '250': { min: 25, mostLikely: 40, max: 70 },
};

export function cfAnchorToRange(anchor: CfAnchor): QuantitativeRange { return CF_ANCHOR_TO_RANGE[anchor]; }

// --- Per-category primary loss currency bands ---------------------------------
// PLM = sum of the 5 applicable primary categories (competitiveAdvantage excluded --
// real but unquantifiable without specific IP valuation data).

const PRODUCTIVITY_RANGE: Record<RatingLevel, QuantitativeRange> = {
  VL: { min: 0, mostLikely: 500, max: 2_000 },
  L: { min: 500, mostLikely: 2_000, max: 8_000 },
  M: { min: 2_000, mostLikely: 8_000, max: 25_000 },
  H: { min: 8_000, mostLikely: 30_000, max: 80_000 },
  VH: { min: 15_000, mostLikely: 50_000, max: 150_000 },
};

const RESPONSE_RANGE: Record<RatingLevel, QuantitativeRange> = {
  VL: { min: 0, mostLikely: 500, max: 2_000 },
  L: { min: 1_000, mostLikely: 4_000, max: 12_000 },
  M: { min: 5_000, mostLikely: 18_000, max: 50_000 },
  H: { min: 15_000, mostLikely: 50_000, max: 120_000 },
  VH: { min: 25_000, mostLikely: 70_000, max: 200_000 },
};

const REPLACEMENT_RANGE: Record<RatingLevel, QuantitativeRange> = {
  VL: { min: 0, mostLikely: 200, max: 1_000 },
  L: { min: 500, mostLikely: 2_000, max: 6_000 },
  M: { min: 2_000, mostLikely: 8_000, max: 25_000 },
  H: { min: 8_000, mostLikely: 25_000, max: 70_000 },
  VH: { min: 15_000, mostLikely: 45_000, max: 130_000 },
};

const FINES_RANGE: Record<RatingLevel, QuantitativeRange> = {
  VL: { min: 0, mostLikely: 0, max: 2_000 },
  L: { min: 1_000, mostLikely: 5_000, max: 15_000 },
  M: { min: 5_000, mostLikely: 35_000, max: 150_000 },
  H: { min: 30_000, mostLikely: 150_000, max: 900_000 },
  VH: { min: 50_000, mostLikely: 300_000, max: 3_400_000 },
};

const REPUTATION_RANGE: Record<RatingLevel, QuantitativeRange> = {
  VL: { min: 0, mostLikely: 0, max: 2_000 },
  L: { min: 500, mostLikely: 2_000, max: 7_000 },
  M: { min: 2_000, mostLikely: 10_000, max: 35_000 },
  H: { min: 10_000, mostLikely: 40_000, max: 120_000 },
  VH: { min: 20_000, mostLikely: 60_000, max: 180_000 },
};

const SECONDARY_MULTIPLIER: Record<RatingLevel, number> = {
  VL: 0.02, L: 0.08, M: 0.12, H: 0.15, VH: 0.25,
};

export function sumPrimaryLossRanges(q: QualitativeInputs): QuantitativeRange {
  const cats: QuantitativeRange[] = [
    PRODUCTIVITY_RANGE[q.productivity],
    RESPONSE_RANGE[q.response],
    REPLACEMENT_RANGE[q.replacement],
    FINES_RANGE[q.finesJudgements],
    REPUTATION_RANGE[q.reputation],
  ];
  return {
    min: cats.reduce((s, c) => s + c.min, 0),
    mostLikely: cats.reduce((s, c) => s + c.mostLikely, 0),
    max: cats.reduce((s, c) => s + c.max, 0),
  };
}

export function deriveSecondaryLossRange(q: QualitativeInputs, primary: QuantitativeRange): QuantitativeRange {
  const m = SECONDARY_MULTIPLIER[q.secondaryLef];
  return { min: primary.min * m, mostLikely: primary.mostLikely * m, max: primary.max * m };
}

export function deriveQuantitativeFromQualitative(q: QualitativeInputs, cfAnchor?: CfAnchor): QuantitativeInputs {
  const primaryLM = sumPrimaryLossRanges(q);
  const secondaryLM = deriveSecondaryLossRange(q, primaryLM);
  return {
    contactFrequencyPerYear: cfAnchor ? cfAnchorToRange(cfAnchor) : cfRatingToRange(q.contactFrequency),
    probabilityOfAction: paRatingToRange(q.probabilityOfAction),
    threatCapability: tcRatingToRange(q.threatCapability),
    controlStrength: csRatingToRange(q.controlStrength),
    primaryLossMagnitude: primaryLM,
    secondaryLossMagnitude: secondaryLM,
  };
}

// --- FAIR result -> qualitative risk-register bridge -----------------------------
// Mirrors the FAIR app's own "Apply to Risk" mapping (RiskFairPanel.tsx).

export const FAIR_TO_LIKELIHOOD: Record<RatingLevel, string> = {
  VL: 'Rare', L: 'Unlikely', M: 'Possible', H: 'Likely', VH: 'Almost Certain',
};
export const FAIR_TO_IMPACT: Record<RatingLevel, string> = {
  VL: 'Negligible', L: 'Minor', M: 'Moderate', H: 'Major', VH: 'Severe',
};

export const FAIR_TOOLTIPS: Record<string, string> = {
  contactFrequency: 'How often does a threat actor attempt this specific attack scenario against your organisation? Rate discrete attack attempts, not how often a channel (email, WiFi) is used.\n\nVL Rarely - once every year or two\nL Occasionally - 1-2 per year\nM Regularly - a few times per year\nH Frequently - roughly quarterly\nVH Constantly - bi-monthly or more',
  probabilityOfAction: 'Given contact, how likely is the threat agent to take a hostile action against this asset?\n\nVL Very unlikely (<10%)\nL Unlikely (10-30%)\nM Possible (30-60%)\nH Likely (60-85%)\nVH Near-certain (>85%)',
  threatCapability: 'How capable is the threat agent relative to what is required to exploit this specific asset?\n\nVL Script kiddie tools against a hardened system\nL Basic attacker, commodity malware\nM Social engineering, can chain exploits\nH Custom tools, bypasses EDR, targeted campaigns\nVH Zero-days, supply chain compromise, nation-state elite',
  controlStrength: 'How effective are the controls protecting this asset against this specific threat?\n\nVL No controls\nL Basic controls with known gaps\nM Standard controls, not fully tested\nH Strong layered controls (MFA, EDR, pen testing)\nVH Best-in-class - zero trust, continuous monitoring',
  productivity: 'How much working time and direct revenue is lost while systems are down or staff are diverted?\n\nVL EUR 0-2K\nL EUR 500-8K\nM EUR 2K-25K\nH EUR 8K-80K\nVH EUR 15K-150K',
  response: 'Costs to detect, contain, investigate, and recover from the incident.\n\nVL EUR 0-2K - internal team only\nL EUR 1K-12K - external IR engaged\nM EUR 5K-50K - forensics, legal, notifications\nH EUR 15K-120K - major breach response\nVH EUR 25K-200K - prolonged investigation',
  replacement: 'Cost to rebuild, restore, or re-provision systems and data.\n\nVL EUR 0-1K\nL EUR 500-6K\nM EUR 2K-25K\nH EUR 8K-70K\nVH EUR 15K-130K',
  competitiveAdvantage: 'Loss from stolen intellectual property, trade secrets, or strategic plans.\n\nVL Minimal\nL Low\nM Moderate - roadmap/pricing exposed\nH High - core IP stolen\nVH Severe - key differentiator lost',
  finesJudgements: 'Direct financial penalties from regulators or courts. VH max reflects a GDPR-scale ceiling (4% of turnover).\n\nVL None\nL EUR 1K-15K\nM EUR 5K-150K\nH EUR 30K-900K\nVH EUR 50K-3.4M',
  reputation: 'Long-term loss of customer trust, revenue, and brand value from public knowledge of the incident.\n\nVL EUR 0-2K\nL EUR 500-7K\nM EUR 2K-35K\nH EUR 10K-120K\nVH EUR 20K-180K',
  secondaryLegal: 'Lawsuits or compensation claims filed by affected customers, partners, or shareholders.\n\nVL Very unlikely\nL Low - a handful of complaints\nM Moderate - class action possible\nH High - multiple large claims\nVH Very High - major class action',
  secondaryRegulatory: 'Regulatory investigations, audits, or operating restrictions beyond any direct fine.\n\nVL None\nL Low - follow-up audit\nM Moderate - mandatory external audit\nH High - operating restrictions\nVH Severe - licence suspension',
  secondaryLef: 'Given a loss event occurs, how likely are external parties to take action? Drives a secondary cost multiplier.\n\nVL Very unlikely (x2%)\nL Low (x8%)\nM Moderate (x12%)\nH High (x15%)\nVH Near-certain (x25%)',
};
